import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';
import type { User, LoginRequest, LoginResponse } from '@shared/types';

export const useAuthStore = defineStore('auth', () => {
    // State
    const user = ref<User | null>(null);
    const token = ref<string | null>(localStorage.getItem('auth_token'));
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Getters
    const isAuthenticated = computed(() => !!token.value && !!user.value);
    const isAdmin = computed(() => user.value?.role === 'admin');

    // Actions
    async function login(credentials: LoginRequest): Promise<boolean> {
        loading.value = true;
        error.value = null;

        try {
            const response = await api.post<{ success: boolean; data: LoginResponse }>('/api/auth/login', credentials);

            if (response.data.success && response.data.data) {
                const { token: newToken, user: userData } = response.data.data;

                token.value = newToken;
                user.value = userData;
                localStorage.setItem('auth_token', newToken);

                return true;
            }

            error.value = 'Login fehlgeschlagen';
            return false;
        } catch (err: unknown) {
            if (err && typeof err === 'object' && 'response' in err) {
                const axiosError = err as { response?: { data?: { message?: string } } };
                error.value = axiosError.response?.data?.message || 'Login fehlgeschlagen';
            } else {
                error.value = 'Ein Fehler ist aufgetreten';
            }
            return false;
        } finally {
            loading.value = false;
        }
    }

    async function logout(): Promise<void> {
        try {
            await api.post('/api/auth/logout');
        } catch {
            // Ignore errors on logout
        } finally {
            token.value = null;
            user.value = null;
            localStorage.removeItem('auth_token');
        }
    }

    async function verifyToken(): Promise<boolean> {
        if (!token.value) {
            return false;
        }

        try {
            const response = await api.get<{ success: boolean; data: User }>('/api/auth/verify');

            if (response.data.success && response.data.data) {
                user.value = response.data.data;
                return true;
            }

            // Token invalid, clear it
            token.value = null;
            user.value = null;
            localStorage.removeItem('auth_token');
            return false;
        } catch {
            token.value = null;
            user.value = null;
            localStorage.removeItem('auth_token');
            return false;
        }
    }

    return {
        user,
        token,
        loading,
        error,
        isAuthenticated,
        isAdmin,
        login,
        logout,
        verifyToken
    };
});
